import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FiArrowRight,
  FiArrowLeft,
  FiClock,
  FiExternalLink,
  FiLink,
  FiMapPin,
  FiPackage,
  FiUser,
} from "react-icons/fi";
import AccountNav from "./AccountNav";
import {
  customers,
  formatAccountDate,
  formatOrderAmount,
  orders,
} from "../Datas/accountData";
import siteConfig from "../config/siteConfig";
import "../css/account.scss";

function CustomerDetail() {
  const { customerId } = useParams();
  const customer = customers.find((item) => item.id === customerId);
  const customerOrders = orders.filter(
    (order) => order.sellerId === customerId
  );

  useEffect(() => {
    document.title = customer
      ? `${customer.name} — ${siteConfig.brandName}`
      : `Customer not found — ${siteConfig.brandName}`;
  }, [customer]);

  if (!customer) {
    return (
      <main className="account-page">
        <div className="shell account-empty">
          <h1>Customer not found</h1>
          <Link className="btn" to="/customers">
            Return to customers
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="account-page">
      <div className="shell">
        <AccountNav />
        <Link className="account-back" to="/customers">
          <FiArrowLeft /> All customers
        </Link>
        <header className="customer-heading">
          <span className="customer-heading__avatar">
            <FiUser />
          </span>
          <div>
            <p className="eyebrow">ERP customer</p>
            <h1>{customer.name}</h1>
            <span className="status-badge status-badge--success">
              {customer.status}
            </span>
          </div>
        </header>

        <div className="customer-detail-grid">
          <section className="account-card customer-profile">
            <h2>Customer information</h2>
            <dl>
              <div>
                <dt>
                  <FiUser /> Contact
                </dt>
                <dd>{customer.contact}</dd>
              </div>
              <div>
                <dt>
                  <FiMapPin /> Address
                </dt>
                <dd>{customer.address}</dd>
              </div>
              <div>
                <dt>
                  <FiClock /> First visit
                </dt>
                <dd>{formatAccountDate(customer.firstVisit)}</dd>
              </div>
            </dl>
          </section>

          <aside className="account-card store-card">
            <span className="store-card__icon">
              <FiLink />
            </span>
            <p className="eyebrow">Buyer storefront</p>
            <h2>Customer store</h2>

            {customer.storeUrl ? (
              <a
                className="btn"
                href={customer.storeUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={`Visit ${customer.name} store`}
              >
                {customer.storeLabel || customer.storeUrl} <FiExternalLink />
              </a>
            ) : (
              <>
                <p>
                  The storefront link has not been supplied yet. This record is
                  ready to display it once available.
                </p>
                <span className="store-card__pending">Store link pending</span>
              </>
            )}
          </aside>
        </div>

        <section className="customer-orders" aria-label="Customer orders">
          <div className="customer-orders__heading">
            <div>
              <p className="eyebrow">Order history</p>
              <h2>Orders for {customer.name}</h2>
            </div>
            <span>{customerOrders.length} order{customerOrders.length === 1 ? "" : "s"}</span>
          </div>

          {customerOrders.length > 0 ? (
            <div className="customer-orders__list">
              {customerOrders.map((order) => (
                <article key={order.id}>
                  <span className="customer-orders__icon">
                    <FiPackage />
                  </span>
                  <div>
                    <small>Order</small>
                    <strong>{order.id}</strong>
                    <p>{order.code}</p>
                  </div>
                  <div>
                    <small>Customer</small>
                    <strong>{order.customer}</strong>
                    <p>{order.product} · {order.quantityLabel}</p>
                  </div>
                  <div>
                    <small>Issued</small>
                    <strong>{formatAccountDate(order.issuedAt)}</strong>
                    <p>{order.carrierStatus}</p>
                  </div>
                  <strong className="customer-orders__amount">
                    {formatOrderAmount(order.amount)}
                  </strong>
                  <Link to={`/orders/${order.id}`}>
                    View order <FiArrowRight />
                  </Link>
                </article>
              ))}
            </div>
          ) : (
            <p className="customer-orders__empty">No orders are linked to this customer.</p>
          )}
        </section>
      </div>
    </main>
  );
}

export default CustomerDetail;
